import { DataCard, Spacer, Tag } from "@qivia/ui";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import styled from "styled-components";
import "../../../../i18n/i18n";
import { AdvancedAnalyticsVehicles } from "../fleetApi";
import { isMissingContractData } from "../lib";
import {
  ArrowDownwardOutlined,
  ArrowUpwardOutlined,
  CloseOutlined,
} from "@qivia/ui/src/designSystem/materialUi/materialUi";

interface AdvancedAnalyticsSummaryProps {
  vehiclesList: AdvancedAnalyticsVehicles[];
}

export const AdvancedAnalyticsSummary = (
  props: AdvancedAnalyticsSummaryProps,
) => {
  const { t } = useTranslation();

  const underrollingCount = useMemo(
    () =>
      props.vehiclesList.filter((v) => v.mileageDeviation === "UNDERROLLING")
        .length,
    [props.vehiclesList],
  );

  const overrollingCount = useMemo(
    () =>
      props.vehiclesList.filter((v) => v.mileageDeviation === "OVERROLLING")
        .length,
    [props.vehiclesList],
  );

  const noContractDataCount = useMemo(
    () => props.vehiclesList.filter((v) => isMissingContractData(v)).length,
    [props.vehiclesList],
  );

  const vehicleLabel = (count: number) =>
    count === 1
      ? t("fleet.advanced_analytics.summary.vehicle")
      : t("fleet.advanced_analytics.summary.vehicles");

  return (
    <StyledContainer>
      <StyledCardWrapper>
        <DataCard
          titleHeight=""
          title={
            <Tag
              text={t("fleet.advanced_analytics.summary.underrolling")}
              iconLeft={<ArrowDownwardOutlined />}
              iconColor={colors["colors/system/warning/warning_dark"]}
              backgroundColor={
                colors["colors/system/warning/warning_ultraLight"]
              }
              textColor={colors["colors/system/warning/warning_dark"]}
            />
          }
          content={
            <StyledCount>
              {underrollingCount} {vehicleLabel(underrollingCount)}
            </StyledCount>
          }
        />
      </StyledCardWrapper>
      <Spacer x={1} />
      <StyledCardWrapper>
        <DataCard
          titleHeight=""
          title={
            <Tag
              text={t("fleet.advanced_analytics.summary.overrolling")}
              iconLeft={<ArrowUpwardOutlined />}
              iconColor={colors["colors/system/error/error_dark"]}
              backgroundColor={colors["colors/system/error/error_ultraLight"]}
              textColor={colors["colors/system/error/error_dark"]}
            />
          }
          content={
            <StyledCount>
              {overrollingCount} {vehicleLabel(overrollingCount)}
            </StyledCount>
          }
        />
      </StyledCardWrapper>
      <Spacer x={1} />
      <StyledCardWrapper>
        <DataCard
          titleHeight=""
          title={
            <Tag
              text={t("fleet.advanced_analytics.summary.noData")}
              iconLeft={<CloseOutlined />}
              iconColor={colors["colors/text/darkGrey"]}
              backgroundColor={
                colors["colors/surfaces/background/background_level2"]
              }
              textColor={colors["colors/text/darkGrey"]}
            />
          }
          content={
            <StyledCount>
              {noContractDataCount} {vehicleLabel(noContractDataCount)}
            </StyledCount>
          }
        />
      </StyledCardWrapper>
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
`;

const StyledCardWrapper = styled.div`
  flex: 1;
`;

const StyledCount = styled.span`
  font-size: 1.5rem;
  font-weight: 600;
`;
